'use client';

import { cn, currencyFormatter } from '@/lib/utils';
import { useCreateChat } from '@/hooks/useCreateChat';
import { useSnackbar } from '@/contexts/SnackbarContext';
import ChatStatusModal from '../ui/ChatStatusModal';
import type { TrendingData } from '../ui/type';
import TrendingCard from './TrendingCard';
import TrendingSkeleton from './TrendingSkeleton';
import type { TrendingCardVariant } from './types';
import { useUserContextStore } from '@/stores/userContextStore';
import { useTranslation } from '@/lib/i18n';

interface TrendingListProps {
  loading: boolean;
  list: TrendingData[];
}

export default function TrendingList({ loading, list }: TrendingListProps) {
  if (loading) {
    return (
      <div className={cn('grid gap-3 grid-cols-3 grid-rows-[140px]')}>
        {[1, 2, 3, 4, 5].map((item) => (
          <TrendingSkeleton key={item} variant={item === 1 ? 'image' : 'text'} />
        ))}
      </div>
    );
  }

  if (list.length) {
    return (
      <div className={cn('grid gap-3 grid-cols-3')}>
        {list.map((item, index) => (
          <TrendingItem key={item.id} trend={item} variant={index === 0 ? 'image' : 'text'} />
        ))}
      </div>
    );
  }

  return null;
}

function TrendingItem({ trend, variant }: { trend: TrendingData; variant: TrendingCardVariant }) {
  const { createChat, isCreating } = useCreateChat();
  const { showError } = useSnackbar();
  const { t } = useTranslation();
  const setUserContext = useUserContextStore((state) => state.setUserContext);

  const buildUserContext = () => {
    if (trend.type === 'crypto') {
      const symbol = trend.crypto?.symbol?.toUpperCase();
      return {
        context_type: 'crypto',
        data: {
          ...trend.crypto,
          token: symbol,
          direction: (trend.crypto?.percent_change_24h ?? 0) >= 0 ? 'up' : 'down',
          trending_card: {
            ...trend.crypto,
            changeText: `${trend.crypto?.percent_change_24h?.toFixed(2)}%`,
            priceText: currencyFormatter(trend.crypto?.price || 0, { notation: 'standard' }),
          },
        },
      };
    }

    if (trend.type === 'market') {
      return {
        context_type: 'market',
        data: trend.market,
      };
    }

    if (trend.type === 'news') {
      return {
        context_type: 'news',
        data: {
          ...trend.news,
          trending_card: trend.news,
        },
      };
    }

    if (trend.type === 'event') {
      return {
        context_type: 'market_topic',
        data: {
          ...trend.event,
          trending_card: trend.event,
        },
      };
    }

    if (trend.type === 'portfolio') {
      return {
        context_type: 'portfolio',
        data: {
          portfolioData: trend.portfolio,
          trending_card: trend.portfolio,
        },
      };
    }

    return null;
  };

  const handleQuery = async () => {
    try {
      const userContext = buildUserContext();
      if (userContext) {
        setUserContext(userContext);
      }

      if (trend.type === 'crypto') {
        await createChat({
          message: trend.query,
          queryType: 'crypto',
          source: `crypto_${trend.crypto?.symbol}`,
        });
        return;
      }
      let sourceId = '';
      if (trend.type === 'market') {
        sourceId = trend.market?.event?.slug ? `market_${trend.market?.event?.slug}` : '';
      } else if (trend.type === 'news') {
        sourceId = trend.news?.news_id ? `news_${trend.news?.news_id}` : '';
      } else if (trend.type === 'event') {
        sourceId = trend.event?.event_id ? `event_${trend.event?.event_id}` : '';
      } else if (trend.type === 'portfolio') {
        sourceId = 'portfolio';
      }
      await createChat({ message: trend.query, source: sourceId });
    } catch (err) {
      showError(t('create chat failed'));
    }
  };

  return (
    <>
      <TrendingCard trend={trend} variant={variant} onClick={handleQuery} />
      {isCreating && <ChatStatusModal />}
    </>
  );
}
